/* GENERATED from src/js/ndog-review-slider.js — edit the source, then run `make js`. Never edit this file by hand. */
/*
  ndog-review-slider.js
  Self-contained web component for the swipeable review row nested inside each slide
  of the NDOG Tiled Slider. Drives a CSS scroll-snap track via optional arrows + dots.
  Swiping itself is native CSS (works without this script); the script powers the
  controls, keeps the active dot in sync, and runs optional per-breakpoint autoplay
  (pauses on interaction / hidden tab / off-screen). No global side effects.
*/
if (!customElements.get('ndog-review-slider')) {
  customElements.define(
    'ndog-review-slider',
    class NdogReviewSlider extends HTMLElement {
      // Cache elements, bail out early if there is nothing to slide.
      connectedCallback() {
        this.track = this.querySelector('[data-review-track]');
        this.cards = Array.from(this.querySelectorAll('.ndog-slide__review'));
        this.arrows = Array.from(this.querySelectorAll('[data-review-dir]'));
        this.dotsContainer = this.querySelector('[data-review-dots]');
        if (!this.track || this.cards.length === 0) return;

        this.activeIndex = 0;
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        // Expand/collapse truncated review text via the "read more" button. Bound
        // before the single-card early return so it works on one-review slides too;
        // attached to the component itself, so it is disposed together with it.
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-review-more]');
          if (!button) return;
          const card = button.closest('.ndog-slide__review');
          if (!card) return;
          const expanded = card.classList.toggle('is-expanded');
          button.setAttribute('aria-expanded', String(expanded));
          button.textContent = expanded ? button.dataset.labelLess : button.dataset.labelMore;
        });

        // A single card needs no controls — hide them and skip wiring.
        if (this.cards.length <= 1) {
          this.hideControls();
          return;
        }

        this.buildDots();
        this.bindEvents();
        this.observeCards();
        this.setActive(0);
        this.resetToStart();
        this.setupAutoplay();
      }

      // Clean up timers + observers so nothing leaks when the section re-renders.
      disconnectedCallback() {
        this.stopAutoplay();
        if (this.observer) this.observer.disconnect();
        if (this.autoplayObserver) this.autoplayObserver.disconnect();
        if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
      }

      // Remove arrow + dot containers when they serve no purpose.
      hideControls() {
        this.querySelectorAll('.ndog-slide__reviews-footer, .ndog-slide__reviews-arrows, .ndog-slide__reviews-dots')
          .forEach((el) => { el.style.display = 'none'; });
      }

      // Create one dot button per card inside the (optional) dots container.
      buildDots() {
        if (!this.dotsContainer) { this.dots = []; return; }
        this.dots = this.cards.map((_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'ndog-slide__reviews-dot';
          dot.dataset.reviewDot = String(i);
          dot.setAttribute('aria-label', String(i + 1));
          this.dotsContainer.appendChild(dot);
          return dot;
        });
      }

      // Wire arrows and dots to the scroll logic.
      bindEvents() {
        this.arrows.forEach((arrow) => {
          arrow.addEventListener('click', () => {
            arrow.dataset.reviewDir === 'next' ? this.next() : this.prev();
          });
        });
        this.dots.forEach((dot) => {
          dot.addEventListener('click', () => this.goToIndex(Number(dot.dataset.reviewDot)));
        });
        // Hold autoplay while the user is interacting; resume when they leave.
        ['pointerenter', 'focusin', 'touchstart'].forEach((evt) => {
          this.addEventListener(evt, () => this.pauseAutoplay(), { passive: true });
        });
        ['pointerleave', 'focusout'].forEach((evt) => {
          this.addEventListener(evt, () => this.resumeAutoplay());
        });
      }

      // Scroll the track so the card at index becomes the snapped one.
      goToIndex(index) {
        if (index < 0) index = this.cards.length - 1;
        if (index >= this.cards.length) index = 0;
        const card = this.cards[index];
        if (!card) return;
        // Subtract the track's scroll-padding so the card lands on its inset snap
        // point (peek) instead of flush against the edge.
        const pad = parseFloat(getComputedStyle(this.track).scrollPaddingLeft) || 0;
        const left = card.getBoundingClientRect().left
          - this.track.getBoundingClientRect().left + this.track.scrollLeft - pad;
        this.track.scrollTo({ left, behavior: this.reduceMotion ? 'auto' : 'smooth' });
        this.setActive(index);
      }

      next() { this.goToIndex(this.activeIndex + 1); }
      prev() { this.goToIndex(this.activeIndex - 1); }

      // Reflect the active card on the dots.
      setActive(index) {
        this.activeIndex = index;
        this.dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
      }

      // Browsers restore the track's scrollLeft across reloads — force back to
      // the first card. Deferred so it runs after layout + scroll restoration.
      resetToStart() {
        requestAnimationFrame(() => {
          this.track.scrollLeft = 0;
          this.setActive(0);
        });
      }

      // Keep the active dot in sync while the user swipes the track.
      observeCards() {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                const index = this.cards.indexOf(entry.target);
                if (index !== -1) this.setActive(index);
              }
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.cards.forEach((c) => this.observer.observe(c));
      }

      // Decide whether autoplay should run for the current breakpoint, then drive it
      // only while this carousel is on screen — every slide has its own review slider,
      // but only one is visible at a time, so the off-screen ones stay idle.
      setupAutoplay() {
        if (this.reduceMotion || this.cards.length <= 1) return;
        const isDesktop = window.matchMedia('(min-width: 990px)').matches;
        const enabled = isDesktop
          ? this.dataset.reviewAutoplayDesktop === 'true'
          : this.dataset.reviewAutoplayMobile === 'true';
        if (!enabled) return;

        this.autoplayMs = (Number(this.dataset.reviewAutoplaySpeed) || 5) * 1000;
        this.autoplayAllowed = true; // flips to false while the user interacts
        this.inView = false;

        // Only tick while the carousel is actually in the viewport.
        this.autoplayObserver = new IntersectionObserver(
          (entries) => {
            this.inView = entries.some((entry) => entry.isIntersecting);
            this.inView ? this.startAutoplay() : this.stopAutoplay();
          },
          { threshold: 0.5 }
        );
        this.autoplayObserver.observe(this);

        // Pause when the browser tab is hidden.
        this.onVisibility = () => (document.hidden ? this.stopAutoplay() : this.startAutoplay());
        document.addEventListener('visibilitychange', this.onVisibility);
      }

      // Start the interval, but only when all gates are open (enabled, in view, not
      // paused by interaction, tab visible).
      startAutoplay() {
        if (!this.autoplayMs || this.timer) return;
        if (!this.inView || !this.autoplayAllowed || document.hidden) return;
        this.timer = setInterval(() => this.next(), this.autoplayMs);
      }

      stopAutoplay() {
        if (this.timer) { clearInterval(this.timer); this.timer = null; }
      }

      pauseAutoplay() {
        this.autoplayAllowed = false;
        this.stopAutoplay();
      }

      resumeAutoplay() {
        this.autoplayAllowed = true;
        this.startAutoplay();
      }
    }
  );
}
